import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { isVisible } from '@testing-library/user-event/dist/utils';

type ToastType = 'success' | 'error' | 'info';
export interface IToastOpcoes {type: ToastType, visible: boolean, mensagem: string }
interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose: () => void;
  isVisible: boolean;
}

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div<{ type: ToastType; isVisible: boolean }>`
  position: fixed;
  top: 20px;
  right: 20px;
  min-width: 300px;
  padding: 16px;
  border-radius: 8px;
  display: ${(props) => props.isVisible ? "flex" : "None"};
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: ${props => props.isVisible ? slideIn : slideOut} 0.3s ease-in-out;
  z-index: 99999;
  
  ${props => {
    switch (props.type) {
      case 'success':
        return `
          background-color: #DFF2BF;
          color: #4F8A10;
        `;
      case 'error':
        return `
          background-color: #FFE8E6;
          color: #D63301;
        `;
      default:
        return `
          background-color: #BDE5F8;
          color: #00529B;
        `;
    }
  }}
`;

const Message = styled.p`
  margin: 0;
  flex-grow: 1;
  font-size: 14px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

export const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 3000,
  onClose,
  isVisible
}) => {
  useEffect(() => {
    if (isVisible && duration) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onClose, isVisible]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle size={20} />;
      case 'error':
        return <AlertCircle size={20} />;
      default:
        return <Info size={20} />;
    }
  };

  return (
    <ToastContainer type={type} isVisible={isVisible}>
      {getIcon()}
      <Message>{message}</Message>
      <CloseButton onClick={onClose} aria-label="Close">
        <X size={18} />
      </CloseButton>
    </ToastContainer>
  );
};